import { Component, Input, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HourConstants } from '../../../../../common/hours.constants';
import { MinutesConstants } from '../../../../../common/minutes.constants';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { SchedulerOptionsComponent } from '../scheduler-options-component';
import { DayOfWeek, Frequency, HourlySchedulerDefinition, SchedulerDefinition } from '../../../../api/scheduler-service';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';

@Component({
  selector: 'hourly-schedule-options',
  imports: [
    CommonModule, MatCardModule, MatLabel, MatSlideToggleModule, MatFormField,
    MatButtonToggleModule, ReactiveFormsModule, FormsModule, MatFormFieldModule, MatInputModule,
    MatChipsModule, MatIconModule, MatTooltipModule, NgxMatTimepickerModule,
  ],
  templateUrl: './hourly-schedule-options.component.html',
  styleUrl: './hourly-schedule-options.component.css'
})
export class HourlyScheduleOptionsComponent implements OnInit, SchedulerOptionsComponent {

  @Input() valueChange : Function | undefined;
  weekdays = Object.values(DayOfWeek);

  interval : FormControl = new FormControl(1, Validators.min(1));
  hours = HourConstants.hours;
  minutes = MinutesConstants.minutes;

  schedulerForm : FormGroup = new FormGroup({});
  dependency: FormControl = new FormControl(false);

  /** Collapsed by default — grids expand on click for picking. */
  hoursExpanded = false;
  minutesExpanded = false;

  /** "Every N hours starting at hour H" custom-preset mini-form. Lets the user
   *  build any periodic-hours pattern without picking 12 chips, and covers
   *  arbitrary offsets (e.g. "every 2 hours starting 7 AM" → [7,9,11,13,15,17,19,21,23]). */
  customRepeatExpanded = false;
  customEveryN = 2;
  customStartHour = 0;

  weekdaysControl : FormControl =  new FormControl([], [Validators.required]);
  hoursControl: FormControl  = new FormControl([], []);
  minutesControl : FormControl  = new FormControl([]);
  executeAtModeControl : FormControl  = new FormControl('executeAt');
  executeEveryMinutesControl : FormControl = new FormControl(1, [Validators.min(1)]);
  /** Rolling anchor for Execute Every mode — "HH:mm" string the wire format
   *  expects. Empty = midnight (legacy behavior). Only meaningful when mode
   *  is Execute Every; ignored in Execute At. */
  anchorTimeControl : FormControl = new FormControl('');
  hourlyOptionsForm: FormGroup = new FormGroup({
    "interval" : this.interval,
    "selectedWeekdays" : this.weekdaysControl,
    "hours" : this.hoursControl,
    "minutes" : this.minutesControl,
    "executeEveryMinutes" : this.executeEveryMinutesControl,
    "executeAt" : this.executeAtModeControl,
    "anchorTime" : this.anchorTimeControl,
    "dependency" : this.dependency
  });

  constructor() {
  }

  ngOnInit(): void {
    this.hoursControl.valueChanges.subscribe((v) => this.interval?.setValue(1, {emitEvent: false}));

    setTimeout(() => this.hourlyOptionsForm.valueChanges.subscribe(() => {
      if (this.valueChange != undefined) {
        this.valueChange();
      }
    }), 0);
  }

  isHourlyExecuteAt() : boolean {
    return this.executeAtModeControl.value === 'executeAt';
  }

  isHourlyExecuteEvery() : boolean {
    return this.executeAtModeControl.value === 'executeEvery';
  }

  getSchedulerDefinition() : SchedulerDefinition {
    const schedulerDefinition: HourlySchedulerDefinition = {
      frequency: Frequency.Hourly,
      interval: this.interval.value,
      weekdays: this.weekdaysControl.value,
      hours: this.hoursControl.value.map((value : string) => this.hours.indexOf(value)),
      executeAt: this.executeAtModeControl.value == 'executeAt',
      executeEveryMinutes: this.executeEveryMinutesControl.value,
      minutes: this.minutesControl.value.map((value : string) => this.minutes.indexOf(value)),
      eventDependency: this.dependency.value,
    };
    // anchorTime isn't on the generated HourlySchedulerDefinition type yet
    // (server spec hasn't been regenerated). Cast so it still makes it onto
    // the wire payload — Jackson on the backend deserialises HH:mm via
    // @JsonFormat. Cast goes away on next client regen.
    const anchor = this.anchorTimeControl.value?.trim();
    if (anchor && this.executeAtModeControl.value === 'executeEvery') {
      (schedulerDefinition as any).anchorTime = anchor;
    }
    return schedulerDefinition;
  }

  getIntervalSuffix() : string {
    return (this.interval.value > 1) ? "hours" : "hour";
  }

  intervalHasError() : boolean {
    return !this.interval.valid;
  }

  intervalErrorMessage() : string {
    if (this.interval.hasError("min")) {
      return "Interval must be 1 or higher";
    }

    return "";
  }

  hourlyOptionsExceuteEveryHasError() : boolean {
    return this.executeEveryMinutesControl.hasError('min');
  }

  hourlyOptionsExceuteEveryErrorMessage() : string {
    if (this.executeEveryMinutesControl.hasError('min')) {
      return "Minutes must be 1 or higher";
    }

    return "";
  }

  // ── Quick-pick presets ─────────────────────────────────────────────────
  // Set whole values rather than mutating the existing array so the chip
  // listbox picks up the change cleanly (it watches reference identity).
  setWeekdays(days: string[]): void {
    this.weekdaysControl.setValue([...days]);
  }
  setHours(values: string[]): void {
    this.hoursControl.setValue([...values]);
  }
  /** 09:00–17:00 inclusive — index 9 through 17 in the AM/PM array. */
  setBusinessHours(): void {
    this.hoursControl.setValue(this.hours.slice(9, 18));
  }
  /** Inverse of business hours. */
  setOffHours(): void {
    const off = [...this.hours.slice(0, 9), ...this.hours.slice(18)];
    this.hoursControl.setValue(off);
  }
  /** "Every N hours starting from offset" pattern — generates the hour-label
   *  list for the chip listbox. e.g. (2, 0) → [0,2,4,…,22] = "every 2 hours
   *  from hour 0"; (2, 7) → [7,9,11,…,23] = "every 2 hours from 7 AM";
   *  (5, 2) → [2,7,12,17,22] = "every 5 hours from hour 2". Stops at hour 23
   *  (no wrap into the next calendar day — Hours of Day is per-day). */
  private buildEveryNFromOffset(everyN: number, offset: number): string[] {
    const picks: string[] = [];
    if (everyN < 1 || offset < 0 || offset > 23) return picks;
    for (let h = offset; h < 24; h += everyN) {
      picks.push(this.hours[h]);
    }
    return picks;
  }

  /** Apply the custom "Every N hours starting at H" mini-form picks to the
   *  Hours of Day chip-listbox. */
  applyCustomRepeat(): void {
    const picks = this.buildEveryNFromOffset(this.customEveryN, this.customStartHour);
    this.hoursControl.setValue(picks);
  }

  /** Live preview shown next to the Apply button, e.g. "[7, 9, 11, … 23]". */
  customRepeatPreview(): string {
    const picks = this.buildEveryNFromOffset(this.customEveryN, this.customStartHour);
    if (picks.length === 0) return '(invalid)';
    const trimmed = picks.map(p => p.trim());
    if (trimmed.length <= 6) return `[${trimmed.join(', ')}]`;
    return `[${trimmed.slice(0, 4).join(', ')}, … ${trimmed[trimmed.length - 1]}]  (${trimmed.length} fires)`;
  }
  setMinutes(values: string[]): void {
    this.minutesControl.setValue([...values]);
  }

  /** Summary string for a collapsed picker — collapses contiguous runs into
   *  ranges so "9 AM, 10 AM, ... 5 PM" reads as "9 AM – 5 PM" instead of a
   *  9-element list. Used by both Hours and Minutes pickers; the labelling
   *  ("hour" vs "minute") and the source array vary, the algorithm doesn't.
   *
   *  @param picks       currently-selected labels from the source array
   *  @param source      full ordered list of available labels
   *  @param unit        singular noun for the count, e.g. "hour" / "minute"
   *  @param emptyAll    summary when nothing or everything is picked
   *  @param formatLabel optional label transformer (default: trim) */
  private rangeSummary(picks: string[],
                       source: string[],
                       unit: string,
                       emptyAll: { empty: string; all: string },
                       formatLabel: (s: string) => string = s => s.trim()): string {
    if (picks.length === 0) return emptyAll.empty;
    if (picks.length === source.length) return emptyAll.all;

    const idxs = picks
      .map(p => source.indexOf(p))
      .filter(i => i >= 0)
      .sort((a, b) => a - b);

    const ranges: string[] = [];
    let start = idxs[0];
    let prev = idxs[0];
    for (let i = 1; i < idxs.length; i++) {
      if (idxs[i] === prev + 1) {
        prev = idxs[i];
        continue;
      }
      ranges.push(start === prev ? formatLabel(source[start])
                                 : `${formatLabel(source[start])} – ${formatLabel(source[prev])}`);
      start = idxs[i];
      prev = idxs[i];
    }
    ranges.push(start === prev ? formatLabel(source[start])
                               : `${formatLabel(source[start])} – ${formatLabel(source[prev])}`);
    return `${picks.length} ${unit}${picks.length === 1 ? '' : 's'}: ${ranges.join(', ')}`;
  }

  hoursSummary(): string {
    return this.rangeSummary(
      this.hoursControl.value || [],
      this.hours,
      'hour',
      { empty: 'Every hour', all: 'All hours' },
    );
  }

  minutesSummary(): string {
    return this.rangeSummary(
      this.minutesControl.value || [],
      this.minutes,
      'minute',
      { empty: 'No specific minute marks', all: 'Every minute' },
      m => `:${m.padStart(2, '0')}`,
    );
  }
}